import React, { useState, useRef, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  IconButton,
  Switch,
  FormControlLabel,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Collapse,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  NewReleases as NewIcon,
  TrendingUp as ImproveIcon,
  BugReport as BugIcon,
  Publish as PublishIcon,
  Mic as MicIcon,
  Stop as StopIcon,
  Delete as DeleteIcon,
  AutoAwesome as AiIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import type { ParseApkMetadataResponse, ReleaseNotesDetailed } from '../../types/AppMobileTypes';

const generateReleaseNotesFn = httpsCallable<
  { audioBase64?: string; text?: string },
  { success: boolean; releaseNotes?: ReleaseNotesDetailed; transcription?: string; error?: string }
>(functions, 'generateReleaseNotes');

interface ReleaseNotesFormProps {
  metadata: ParseApkMetadataResponse;
  onPublish: (releaseNotes: ReleaseNotesDetailed, forceUpdate: boolean) => Promise<void>;
  isPublishing: boolean;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface NoteSectionProps {
  title: string;
  icon: React.ReactNode;
  color: string;
  items: string[];
  onAdd: (text: string) => void;
  onRemove: (index: number) => void;
}

const NoteSection: React.FC<NoteSectionProps> = ({ title, icon, color, items, onAdd, onRemove }) => {
  const [inputValue, setInputValue] = useState('');

  const handleAdd = () => {
    const trimmed = inputValue.trim();
    if (trimmed) {
      onAdd(trimmed);
      setInputValue('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Box sx={{ color }}>{icon}</Box>
        <Typography variant="subtitle2">{title}</Typography>
        <Chip label={items.length} size="small" sx={{ height: 20, fontSize: 11 }} />
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        <TextField
          size="small"
          fullWidth
          placeholder={`Aggiungi ${title.toLowerCase()}...`}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <Button
          variant="outlined"
          size="small"
          onClick={handleAdd}
          disabled={!inputValue.trim()}
          sx={{ minWidth: 40 }}
        >
          <AddIcon fontSize="small" />
        </Button>
      </Box>

      {items.map((item, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: 0.5,
            px: 1,
            mb: 0.5,
            bgcolor: 'grey.50',
            borderRadius: 1,
            borderLeft: `3px solid ${color}`,
          }}
        >
          <Typography variant="body2" sx={{ flex: 1 }}>
            {item}
          </Typography>
          <IconButton size="small" onClick={() => onRemove(index)}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      ))}
    </Box>
  );
};

type AiState = 'idle' | 'recording' | 'processing';

interface AiInputPanelProps {
  onNotesGenerated: (notes: ReleaseNotesDetailed) => void;
  disabled?: boolean;
}

const AiInputPanel: React.FC<AiInputPanelProps> = ({ onNotesGenerated, disabled = false }) => {
  const theme = useTheme();
  const [aiState, setAiState] = useState<AiState>('idle');
  const [textInput, setTextInput] = useState('');
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [transcription, setTranscription] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const startRecording = useCallback(async () => {
    setError(null);
    setTranscription(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : 'audio/webm',
      });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;

        if (chunksRef.current.length === 0) {
          setAiState('idle');
          return;
        }

        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        await processAudio(blob);
      };

      mediaRecorder.start(1000);
      setAiState('recording');
      setRecordingDuration(0);

      timerRef.current = setInterval(() => {
        setRecordingDuration((prev) => prev + 1);
      }, 1000);
    } catch {
      setError('Impossibile accedere al microfono');
      setAiState('idle');
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
  }, []);

  const cancelRecording = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.ondataavailable = null;
      mediaRecorderRef.current.onstop = null;
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    chunksRef.current = [];
    setAiState('idle');
    setRecordingDuration(0);
  }, []);

  const processAudio = async (blob: Blob) => {
    setAiState('processing');
    try {
      const buffer = await blob.arrayBuffer();
      const base64 = btoa(
        new Uint8Array(buffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
      );

      const result = await generateReleaseNotesFn({ audioBase64: base64 });

      if (result.data.success && result.data.releaseNotes) {
        onNotesGenerated(result.data.releaseNotes);
        if (result.data.transcription) {
          setTranscription(result.data.transcription);
        }
      } else {
        setError(result.data.error || 'Nessuna nota estratta dalla registrazione');
      }
    } catch (err: any) {
      console.error('[ReleaseNotesForm] Errore generazione AI (audio):', err);
      setError(err?.message?.includes('sovraccarico') || err?.code === 'unavailable'
        ? 'Servizio AI temporaneamente sovraccarico, riprova tra qualche secondo'
        : 'Errore durante la generazione delle note');
    } finally {
      setAiState('idle');
    }
  };

  const processText = async () => {
    if (!textInput.trim()) return;
    setError(null);
    setTranscription(null);
    setAiState('processing');

    try {
      const result = await generateReleaseNotesFn({ text: textInput.trim() });

      if (result.data.success && result.data.releaseNotes) {
        onNotesGenerated(result.data.releaseNotes);
        setTextInput('');
      } else {
        setError(result.data.error || 'Nessuna nota estratta dal testo');
      }
    } catch (err: any) {
      console.error('[ReleaseNotesForm] Errore generazione AI (testo):', err);
      setError(err?.message?.includes('sovraccarico') || err?.code === 'unavailable'
        ? 'Servizio AI temporaneamente sovraccarico, riprova tra qualche secondo'
        : 'Errore durante la generazione delle note');
    } finally {
      setAiState('idle');
    }
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const isProcessing = aiState === 'processing';

  return (
    <Box
      sx={{
        p: 2,
        mb: 2,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: alpha(theme.palette.primary.main, 0.02),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <AiIcon sx={{ fontSize: 18, color: 'primary.main' }} />
        <Typography variant="subtitle2" color="primary.main">
          Genera con AI
        </Typography>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Descrivi le modifiche a voce o per iscritto. L'AI classifichera automaticamente novita, miglioramenti e correzioni.
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        {aiState === 'idle' && (
          <IconButton
            onClick={startRecording}
            disabled={disabled || isProcessing}
            size="small"
            sx={{
              mt: 0.5,
              bgcolor: alpha(theme.palette.error.main, 0.1),
              color: 'error.main',
              '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.2) },
            }}
          >
            <MicIcon fontSize="small" />
          </IconButton>
        )}

        {aiState === 'recording' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            <IconButton
              onClick={stopRecording}
              size="small"
              sx={{
                bgcolor: 'error.main',
                color: 'white',
                animation: 'pulse 1.5s infinite',
                '@keyframes pulse': {
                  '0%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.error.main, 0.4)}` },
                  '70%': { boxShadow: `0 0 0 8px ${alpha(theme.palette.error.main, 0)}` },
                  '100%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.error.main, 0)}` },
                },
                '&:hover': { bgcolor: 'error.dark' },
              }}
            >
              <StopIcon fontSize="small" />
            </IconButton>
            <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, minWidth: 36 }}>
              {formatTime(recordingDuration)}
            </Typography>
            <IconButton onClick={cancelRecording} size="small" color="default">
              <DeleteIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        )}

        {aiState === 'processing' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <CircularProgress size={20} />
          </Box>
        )}

        {aiState !== 'recording' && (
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={1}
            maxRows={4}
            placeholder={isProcessing ? 'Elaborazione in corso...' : 'Oppure descrivi le modifiche qui...'}
            value={textInput}
            onChange={(e) => setTextInput(e.target.value)}
            disabled={disabled || isProcessing}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                processText();
              }
            }}
          />
        )}

        {aiState !== 'recording' && (
          <IconButton
            onClick={processText}
            disabled={disabled || isProcessing || !textInput.trim()}
            size="small"
            color="primary"
            sx={{ mt: 0.5 }}
          >
            <SendIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Collapse in={!!transcription}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
          Trascrizione: "{transcription}"
        </Typography>
      </Collapse>

      {error && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export const ReleaseNotesForm: React.FC<ReleaseNotesFormProps> = ({
  metadata,
  onPublish,
  isPublishing,
}) => {
  const [novita, setNovita] = useState<string[]>([]);
  const [miglioramenti, setMiglioramenti] = useState<string[]>([]);
  const [correzioni, setCorrezioni] = useState<string[]>([]);
  const [forceUpdate, setForceUpdate] = useState(false);

  const totalNotes = novita.length + miglioramenti.length + correzioni.length;

  const handlePublish = async () => {
    await onPublish(
      { novita, miglioramenti, correzioni },
      forceUpdate
    );
  };

  const handleAiNotesGenerated = useCallback((notes: ReleaseNotesDetailed) => {
    if (notes.novita.length > 0) {
      setNovita((prev) => [...prev, ...notes.novita]);
    }
    if (notes.miglioramenti.length > 0) {
      setMiglioramenti((prev) => [...prev, ...notes.miglioramenti]);
    }
    if (notes.correzioni.length > 0) {
      setCorrezioni((prev) => [...prev, ...notes.correzioni]);
    }
  }, []);

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Dettagli rilascio
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Versione</Typography>
            <Typography variant="body1" fontWeight={600}>
              {metadata.versionName}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Codice versione</Typography>
            <Typography variant="body1" fontWeight={600}>
              {metadata.versionCode}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Pacchetto</Typography>
            <Typography variant="body1" fontWeight={600} sx={{ fontFamily: 'monospace', fontSize: 13 }}>
              {metadata.packageName}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Dimensione</Typography>
            <Typography variant="body1" fontWeight={600}>
              {formatBytes(metadata.sizeBytes)}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Typography variant="subtitle1" gutterBottom>
          Note di rilascio
        </Typography>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Opzionali. Puoi generarle con AI, aggiungerle manualmente, o pubblicare senza note.
        </Typography>

        <AiInputPanel
          onNotesGenerated={handleAiNotesGenerated}
          disabled={isPublishing}
        />

        <NoteSection
          title="Novita"
          icon={<NewIcon />}
          color="#4caf50"
          items={novita}
          onAdd={(text) => setNovita((prev) => [...prev, text])}
          onRemove={(i) => setNovita((prev) => prev.filter((_, idx) => idx !== i))}
        />

        <NoteSection
          title="Miglioramenti"
          icon={<ImproveIcon />}
          color="#2196f3"
          items={miglioramenti}
          onAdd={(text) => setMiglioramenti((prev) => [...prev, text])}
          onRemove={(i) => setMiglioramenti((prev) => prev.filter((_, idx) => idx !== i))}
        />

        <NoteSection
          title="Correzioni"
          icon={<BugIcon />}
          color="#ff9800"
          items={correzioni}
          onAdd={(text) => setCorrezioni((prev) => [...prev, text])}
          onRemove={(i) => setCorrezioni((prev) => prev.filter((_, idx) => idx !== i))}
        />

        <Divider sx={{ my: 2 }} />

        <FormControlLabel
          control={
            <Switch
              checked={forceUpdate}
              onChange={(e) => setForceUpdate(e.target.checked)}
              color="warning"
            />
          }
          label={
            <Box>
              <Typography variant="body2" fontWeight={500}>
                Aggiornamento obbligatorio
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Il dialog sull'app non sara dismissibile. Usa solo per aggiornamenti critici.
              </Typography>
            </Box>
          }
          sx={{ ml: 0, mb: 2 }}
        />

        {forceUpdate && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            L'utente sara obbligato ad aggiornare prima di poter usare l'app.
          </Alert>
        )}

        <Button
          variant="contained"
          size="large"
          fullWidth
          startIcon={isPublishing ? <CircularProgress size={20} color="inherit" /> : <PublishIcon />}
          onClick={handlePublish}
          disabled={isPublishing}
          sx={{
            bgcolor: '#4caf50',
            '&:hover': { bgcolor: '#388e3c' },
          }}
        >
          {isPublishing
            ? 'Pubblicazione in corso...'
            : totalNotes === 0
              ? 'Pubblica senza note'
              : 'Pubblica rilascio'
          }
        </Button>
      </CardContent>
    </Card>
  );
};
